import React, { useState, useEffect, useCallback, useDeferredValue } from 'react';
import { Comment } from '../../../types/social';
import CommentInput from './CommentInput';
import CommentReplyInput from './CommentReplyInput';
import CommentActions from './CommentActions';
import CommentReplies from './CommentReplies';
import CommentsSkeleton from './CommentsSkeleton';
import { Button } from '../../ui/button';
import {
  getComments,
  createComment,
  toggleCommentLike,
  createCommentReply
} from '../../../lib/supabase/social';
import { addNotification } from '../../../lib/supabase/notifications';
import { supabase } from '../../../lib/supabase';

interface PostCommentsProps {
  postId: string;
  onCommentAdded?: () => void;
}

const PostComments: React.FC<PostCommentsProps> = ({ postId, onCommentAdded }) => {
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    getComments(postId)
      .then(({ comments: initial, hasMore }) => {
        setComments(initial);
        setHasMore(hasMore);
      })
      .catch(err => {
        console.error(err);
        setError('Failed to load comments');
      })
      .finally(() => setIsLoading(false));
  }, [postId]);

  const loadMoreComments = useCallback(async () => {
    if (!hasMore || isLoading) return;
    setIsLoading(true);
    try {
      const nextPage = page + 1;
      const { comments: more, hasMore: moreFlag } = await getComments(postId, nextPage);
      setComments(prev => [...prev, ...more]);
      setHasMore(moreFlag);
      setPage(nextPage);
    } catch {
      setError('Failed to load more comments');
    } finally {
      setIsLoading(false);
    }
  }, [hasMore, isLoading, page, postId]);

  const handleNewComment = useCallback(
    async (text: string, mentions?: { full_name: string; auth_id: string }[]) => {
      setIsSubmitting(true);
      setError(null);
      try {
        const newComment = await createComment(postId, text, mentions);
        setComments(prev =>
          prev.some(c => c.id === newComment.id) ? prev : [...prev, newComment]
        );
        onCommentAdded?.();
      } catch (err) {
        console.error(err);
        setError('Failed to post comment');
      } finally {
        setIsSubmitting(false);
      }
    },
    [postId, onCommentAdded]
  );

  const handleLikeComment = useCallback(
    async (commentId: string) => {
      const idx = comments.findIndex(c => c.id === commentId);
      if (idx === -1) return;
      const target = comments[idx];
      const newIsLiked = !target.isLiked;
      const updated = [...comments];
      updated[idx] = {
        ...target,
        isLiked: newIsLiked,
        likes: target.likes + (newIsLiked ? 1 : -1)
      };
      setComments(updated);
      try {
        const result = await toggleCommentLike(commentId);
        if (result !== newIsLiked) {
          updated[idx] = target;
          setComments(updated);
        }
      } catch (err) {
        console.error(err);
      }
    },
    [comments]
  );

  const deferred = useDeferredValue(comments);

  return (
    <div className="border-t border-dark-300">
      <div className="p-4 space-y-4">
        <CommentInput isSubmitting={isSubmitting} onSubmit={handleNewComment} />

        <div className="space-y-4">
          {isLoading ? (
            <CommentsSkeleton count={3} />
          ) : (
            deferred.map(c => (
              <div key={c.id} className="flex space-x-3">
                <img
                  src={c.author.image}
                  alt={c.author.name}
                  className="w-8 h-8 rounded-full object-cover border border-dark-400"
                  loading="lazy"
                />
                <div className="flex-1">
                  <div className="bg-dark-300 rounded-2xl px-4 py-2.5">
                    <div className="font-medium text-white">{c.author.name}</div>
                    <p className="text-white text-sm">{c.content}</p>
                  </div>

                  <CommentActions
                    commentId={c.id}
                    isLiked={c.isLiked}
                    likesCount={c.likes}
                    repliesCount={c.repliesCount}
                    timestamp={c.createdAt}
                    onLike={() => handleLikeComment(c.id)}
                    onReply={() => setReplyingToId(c.id)}
                  />

                  {replyingToId === c.id && (
                    <CommentReplyInput
                      commentId={c.id}
                      onSubmit={async (id, content, mentions) => {
                        try {
                          const newReply = await createCommentReply(id, content);
                          setComments(prev =>
                            prev.map(x =>
                              x.id === id
                                ? { ...x, repliesCount: x.repliesCount + 1 }
                                : x
                            )
                          );

                          if (mentions?.length) {
                            const { data: { user } } = await supabase.auth.getUser();
                            if (!user?.id) throw new Error('Missing sender');

                            await Promise.all(
                              mentions.map(userMention =>
                                addNotification({
                                  type: 'mention',
                                  user_id: userMention.auth_id,
                                  sender_id: user.id,
                                  content: `${user.user_metadata.full_name || 'Someone'} mentioned you in a reply.`,
                                  metadata: {
                                    post_id: postId,
                                    comment_id: id,
                                    reply_id: newReply.id
                                  }
                                })
                              )
                            );
                          }
                        } catch (err) {
                          console.error(err);
                        }
                        setReplyingToId(null);
                      }}
                      onCancel={() => setReplyingToId(null)}
                    />
                  )}

                  <CommentReplies
                    key={`replies-${c.id}-${c.repliesCount}`}
                    commentId={c.id}
                    initialRepliesCount={c.repliesCount}
                  />
                </div>
              </div>
            ))
          )}
        </div>

        {hasMore && (
          <div className="text-center">
            <Button
              onClick={loadMoreComments}
              disabled={isLoading}
              variant="outline"
              className="text-dark-800 hover:text-white"
            >
              Load More Comments
            </Button>
          </div>
        )}

        {error && (
          <div className="text-red-400 text-sm text-center">{error}</div>
        )}
      </div>
    </div>
  );
};

export default PostComments;
